import { useCallback, useReducer } from "react"

import { DEFAULT_VIEWER_MOUSE_MODE, type ViewerMouseMode } from "./mouse-mode"

export type NvSceneUiState = {
  layoutName: string
  activeViewerIndex: number
  dropTargetIndex: number | null
  viewerError: string | null
  mouseMode: ViewerMouseMode
  selectedProtocolId: string | null
  gridOpen: boolean
  crosshairVisible: boolean
  viewerSliceLayouts: Record<number, string>
}

type NvSceneUiAction =
  | { type: "set-layout-name"; value: string }
  | { type: "set-active-viewer-index"; value: number }
  | { type: "set-drop-target-index"; value: number | null }
  | { type: "set-viewer-error"; value: string | null }
  | { type: "set-mouse-mode"; value: ViewerMouseMode }
  | { type: "set-selected-protocol-id"; value: string | null }
  | { type: "set-grid-open"; value: boolean }
  | { type: "set-crosshair-visible"; value: boolean }
  | { type: "set-viewer-slice-layout"; viewerIndex: number; layoutKey: string | null }

const initialNvSceneUiState: NvSceneUiState = {
  layoutName: "2x2",
  activeViewerIndex: 0,
  dropTargetIndex: null,
  viewerError: null,
  mouseMode: DEFAULT_VIEWER_MOUSE_MODE,
  selectedProtocolId: null,
  gridOpen: false,
  crosshairVisible: false,
  viewerSliceLayouts: {}
}

function nvSceneUiReducer(state: NvSceneUiState, action: NvSceneUiAction): NvSceneUiState {
  switch (action.type) {
    case "set-layout-name":
      return { ...state, layoutName: action.value }
    case "set-active-viewer-index":
      return { ...state, activeViewerIndex: action.value }
    case "set-drop-target-index":
      return { ...state, dropTargetIndex: action.value }
    case "set-viewer-error":
      return { ...state, viewerError: action.value }
    case "set-mouse-mode":
      return { ...state, mouseMode: action.value }
    case "set-selected-protocol-id":
      return { ...state, selectedProtocolId: action.value }
    case "set-grid-open":
      return { ...state, gridOpen: action.value }
    case "set-crosshair-visible":
      return { ...state, crosshairVisible: action.value }
    case "set-viewer-slice-layout": {
      const next = { ...state.viewerSliceLayouts }
      if (action.layoutKey === null) {
        delete next[action.viewerIndex]
      } else {
        next[action.viewerIndex] = action.layoutKey
      }
      return { ...state, viewerSliceLayouts: next }
    }
    default:
      return state
  }
}

export function useNvSceneUiState() {
  const [uiState, dispatch] = useReducer(nvSceneUiReducer, initialNvSceneUiState)

  const applyLayoutName = useCallback((value: string) => {
    dispatch({ type: "set-layout-name", value })
  }, [])
  const applyActiveViewerIndex = useCallback((value: number) => {
    dispatch({ type: "set-active-viewer-index", value })
  }, [])
  const applyDropTargetIndex = useCallback((value: number | null) => {
    dispatch({ type: "set-drop-target-index", value })
  }, [])
  const applyViewerError = useCallback((value: string | null) => {
    dispatch({ type: "set-viewer-error", value })
  }, [])
  const applyMouseMode = useCallback((value: ViewerMouseMode) => {
    dispatch({ type: "set-mouse-mode", value })
  }, [])
  const applySelectedProtocolId = useCallback((value: string | null) => {
    dispatch({ type: "set-selected-protocol-id", value })
  }, [])
  const applyGridOpen = useCallback((value: boolean) => {
    dispatch({ type: "set-grid-open", value })
  }, [])
  const applyCrosshairVisible = useCallback((value: boolean) => {
    dispatch({ type: "set-crosshair-visible", value })
  }, [])
  const applyViewerSliceLayout = useCallback((viewerIndex: number, layoutKey: string | null) => {
    dispatch({ type: "set-viewer-slice-layout", viewerIndex, layoutKey })
  }, [])

  return {
    uiState,
    applyLayoutName,
    applyActiveViewerIndex,
    applyDropTargetIndex,
    applyViewerError,
    applyMouseMode,
    applySelectedProtocolId,
    applyGridOpen,
    applyCrosshairVisible,
    applyViewerSliceLayout
  }
}
